import { SelfElectedStateFundingProgramInput } from "@outschool/gql-backend-generated";

import { FundingProgramAbbreviation } from "./FundingProgramProperties";

export function shouldRequireMembershipForEnrollmentBasedOnSelfElectedStateFundingProgram(
  selfElectedStateFundingProgram:
    | SelfElectedStateFundingProgramInput
    | null
    | undefined,
): boolean {
  return (
    !selfElectedStateFundingProgram?.fundingOptions ||
    [
      // used in v1
      selfElectedStateFundingProgram?.fundingOptions?.charter,
      selfElectedStateFundingProgram?.fundingOptions?.statePortal,
      selfElectedStateFundingProgram?.fundingOptions?.reimbursement,
      // used in v2
      selfElectedStateFundingProgram?.fundingOptions?.otherProgramName,
      selfElectedStateFundingProgram?.fundingOptions?.stateDebitCard,
      selfElectedStateFundingProgram?.fundingOptions?.stateProgramName,
    ].every(option => !Boolean(option))
  );
}

export const userUsesSelfElectedPublicFundingOptions = (
  selfElectedStateFundingProgram:
    | SelfElectedStateFundingProgramInput
    | null
    | undefined,
): boolean => {
  return (
    !!selfElectedStateFundingProgram?.fundingOptions &&
    [
      // used in v1
      selfElectedStateFundingProgram?.fundingOptions?.charter,
      selfElectedStateFundingProgram?.fundingOptions?.statePortal,
      selfElectedStateFundingProgram?.fundingOptions?.reimbursement,
      // used in v2
      selfElectedStateFundingProgram?.fundingOptions?.stateDebitCard,
      selfElectedStateFundingProgram?.fundingOptions?.otherProgramName,
      selfElectedStateFundingProgram?.fundingOptions?.stateProgramName,
    ].some(option => Boolean(option))
  );
};

export function wasFundingProgramSelected(
  selfElectedStateFundingProgram:
    | SelfElectedStateFundingProgramInput
    | null
    | undefined,
): boolean {
  if (
    selfElectedStateFundingProgram?.fundingOptions &&
    [
      // used in v1
      selfElectedStateFundingProgram?.fundingOptions?.charter,
      selfElectedStateFundingProgram?.fundingOptions?.statePortal,
      selfElectedStateFundingProgram?.fundingOptions?.reimbursement,
      selfElectedStateFundingProgram?.fundingOptions?.selfPay,
      // used in v2
      selfElectedStateFundingProgram?.fundingOptions?.otherProgramName,
      selfElectedStateFundingProgram?.fundingOptions?.stateProgramName,
      selfElectedStateFundingProgram?.fundingOptions?.stateDebitCard,
    ].some(option => Boolean(option))
  ) {
    return true;
  }
  return false;
}

/**
 * Charter options are booleans, so unlike `stateProgramName` they do not name a
 * funding program. Each state that runs a charter program needs an entry here;
 * California is the only one today.
 *
 * `caCharterFundingPreference` in @outschool/funding-programs writes the
 * preference this reads, so the two have to agree on which state means charter.
 * Both can go away once the funding program catalog records a program type.
 */
const CHARTER_FUNDING_PROGRAM_BY_STATE: Record<
  string,
  FundingProgramAbbreviation
> = {
  CA: FundingProgramAbbreviation.CA,
};

type CharterFundingPreference = Pick<
  SelfElectedStateFundingProgramInput,
  "stateAbbreviation"
> & {
  fundingOptions: Pick<
    SelfElectedStateFundingProgramInput["fundingOptions"],
    "charter"
  >;
};

type CharterFundingProgramCatalogEntry = {
  abbreviation: string;
  programRemoved?: boolean | null;
};

/**
 * Resolves the funding program a charter preference stands for. Charter
 * families are curated by state rather than by an ESA membership, so callers
 * rank this ahead of learner-level programs and IP-based state detection.
 *
 * Callers short-circuit their whole resolution on this value and have no
 * fallback behind it, so a retired program would narrow search to nothing and
 * advertise eligibility that cannot be used. Absence from `programs` is not
 * treated the same way: the catalog is loaded asynchronously and is empty until
 * the query resolves, so requiring a match would drop the preference on every
 * page load.
 */
export function getCharterFundingProgram(
  selfElectedStateFundingProgram: CharterFundingPreference | null | undefined,
  programs: readonly CharterFundingProgramCatalogEntry[],
): FundingProgramAbbreviation | undefined {
  if (!selfElectedStateFundingProgram?.fundingOptions?.charter) {
    return undefined;
  }

  const abbreviation =
    CHARTER_FUNDING_PROGRAM_BY_STATE[
      selfElectedStateFundingProgram.stateAbbreviation
    ];

  if (!abbreviation) {
    return undefined;
  }

  const catalogEntry = programs.find(
    program => program.abbreviation === abbreviation,
  );

  return catalogEntry?.programRemoved ? undefined : abbreviation;
}

export const FUNDING_OPTION_KEYS = {
  stateProgramName: "stateProgramName" as const,
  otherProgramName: "otherProgramName" as const,
  stateDebitCard: "stateDebitCard" as const,
  statePortal: "statePortal" as const,
  reimbursement: "reimbursement" as const,
  charter: "charter" as const,
  selfPay: "selfPay" as const,
} as const;

export type FundingOptionKey = keyof typeof FUNDING_OPTION_KEYS;
